import { Component } from '@theme/component';

class ProductRecommendations extends Component {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);

    // The Liquid fallback is visible immediately. If it is empty, hide the
    // whole section until the recommendations request returns useful cards.
    this.#syncSectionVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  async #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent, url } = this.dataset;

    if (!productId || !sectionId || !url) return;
    if (recommendationsPerformed === 'true') {
      this.#syncSectionVisibility();
      return;
    }

    const requestUrl = `${url}&product_id=${encodeURIComponent(productId)}&section_id=${encodeURIComponent(sectionId)}&intent=${encodeURIComponent(intent || 'related')}`;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(requestUrl, {
        signal: this.#activeFetch.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error(`Server returned ${response.status}`);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fetchedElement = parsed.querySelector(
        `product-recommendations[data-section-id="${CSS.escape(sectionId)}"]`
      );

      // Only replace the existing fallback when Shopify returned actual product
      // cards. If Search & Discovery returns zero eligible products, retain the
      // fallback collection instead of leaving an empty heading.
      if (fetchedElement?.querySelector('.product-card')) {
        this.innerHTML = fetchedElement.innerHTML;
        this.dataset.recommendationsPerformed = 'true';
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Product recommendations error:', error.message);
      }
      // A failed request is non-fatal because the Liquid fallback stays visible.
    } finally {
      this.#activeFetch = null;
      this.#syncSectionVisibility();
    }
  }

  #syncSectionVisibility() {
    const section = this.closest('[data-product-recommendations-section]');
    if (!section) return;
    section.hidden = !this.querySelector('.product-card');
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
